// Base colors that remain consistent across themes
const baseColors = {
  // Primary colors - Modern blue
  primary: '#3B82F6',
  primaryLight: '#60A5FA',
  primaryDark: '#1D4ED8',
  
  // Status colors
  success: '#10B981',
  successLight: '#34D399',
  warning: '#F59E0B',
  warningLight: '#FBBF24',
  error: '#EF4444',
  errorLight: '#F87171',
  info: '#3B82F6',
  infoLight: '#60A5FA',
  
  // Accent colors for labels
  accent1: '#8B5CF6',
  accent2: '#EC4899',
  accent3: '#F59E0B',
  accent4: '#10B981',
  accent5: '#EF4444',
  accent6: '#06B6D4',
  accent7: '#84CC16',
  accent8: '#F97316',
};

// Light theme colors
const lightColors = {
  ...baseColors,
  
  // Neutral colors
  white: '#FFFFFF',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  
  // Background colors
  background: '#FAFAFA',
  surface: '#FFFFFF',
  surfaceHover: '#F9FAFB',
  
  // Text colors
  text: '#111827',
  textSecondary: '#6B7280',
  textMuted: '#9CA3AF',
  
  // Border colors
  border: '#E5E7EB',
  borderLight: '#F3F4F6',
  borderDark: '#D1D5DB',
};

// Dark theme colors
const darkColors = {
  ...baseColors,
  
  // Neutral colors - Modern dark palette with better contrast
  white: '#FFFFFF',
  gray50: '#0A0A0A',
  gray100: '#141414',
  gray200: '#1F1F1F',
  gray300: '#2A2A2A',
  gray400: '#404040',
  gray500: '#525252',
  gray600: '#737373',
  gray700: '#A3A3A3',
  gray800: '#D4D4D4',
  gray900: '#F5F5F5',
  
  // Background colors - Modern dark backgrounds
  background: '#0A0A0A',
  surface: '#111111',
  surfaceHover: '#1A1A1A',
  
  // Text colors - High contrast for readability
  text: '#FFFFFF',
  textSecondary: '#D4D4D4',
  textMuted: '#A3A3A3',
  
  // Border colors - Subtle but visible
  border: '#2A2A2A',
  borderLight: '#404040',
  borderDark: '#1F1F1F',
  
  // Modern dark theme overrides for better visual hierarchy
  primary: '#4F46E5', // More vibrant indigo for dark mode
  primaryLight: '#6366F1',
  primaryDark: '#3730A3',
  
  // Enhanced accent colors for dark mode
  accent1: '#A855F7', // Purple
  accent2: '#EC4899', // Pink
  accent3: '#F59E0B', // Amber
  accent4: '#10B981', // Emerald
  accent5: '#EF4444', // Red
  accent6: '#06B6D4', // Cyan
  accent7: '#84CC16', // Lime
  accent8: '#F97316', // Orange
};

// Common theme properties
const commonTheme = {
  spacing: {
    xs: '0.25rem',   // 4px
    sm: '0.5rem',    // 8px
    md: '1rem',      // 16px
    lg: '1.5rem',    // 24px
    xl: '2rem',      // 32px
    '2xl': '3rem',   // 48px
    '3xl': '4rem',   // 64px
  },
  
  borderRadius: {
    sm: '0.25rem',   // 4px
    md: '0.375rem',  // 6px
    lg: '0.5rem',    // 8px
    xl: '0.75rem',   // 12px
    '2xl': '1rem',   // 16px
    full: '9999px',
  },
  
  typography: {
    fontFamily: {
      sans: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      mono: 'SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
    },
    fontSize: {
      xs: '0.75rem',    // 12px
      sm: '0.875rem',   // 14px
      base: '1rem',     // 16px
      lg: '1.125rem',   // 18px
      xl: '1.25rem',    // 20px
      '2xl': '1.5rem',  // 24px
      '3xl': '1.875rem', // 30px
      '4xl': '2.25rem', // 36px
    },
    fontWeight: {
      normal: 400,
      medium: 500,
      semibold: 600,
      bold: 700,
    },
    lineHeight: {
      tight: 1.25,
      normal: 1.5,
      relaxed: 1.75,
    },
  },
  
  breakpoints: {
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },
};

export const lightTheme = {
  ...commonTheme,
  colors: lightColors,
  
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  },
};

export const darkTheme = {
  ...commonTheme,
  colors: darkColors,
  
  shadows: {
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.5)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.6), 0 2px 4px -1px rgba(0, 0, 0, 0.4)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.6), 0 4px 6px -2px rgba(0, 0, 0, 0.4)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.6), 0 10px 10px -5px rgba(0, 0, 0, 0.4)',
  },
};

// Default theme (for backward compatibility)
export const theme = lightTheme;
